import React from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import cn from 'classnames';
import { matchPath, Redirect, withRouter } from 'react-router-dom';

import DefaultRedirect from '../../../../../../common/router/DefaultRedirect';
import NavRoute from '../../../../../../common/router/Route';
import routes from '../../../../../../../routes';

import viewActions from '../../../../../../../actions/viewActions';
import sceneActions from '../../../../../../../actions/sceneActions';
import viewSettingsActions from '../../../../../../../actions/viewSettingsActions';
import { connect } from '../../../../../../StateProvider';
import ViewHeader from './ViewHeader';
import ViewBody from './ViewBody';

import styles from './view.less';
import { VIEW_MODES } from '../../../../../../../configs/viewSettings';

class View extends React.Component {
  static propTypes = {
    sceneId: PropTypes.string,
    catalogId: PropTypes.string,
    viewId: PropTypes.string,
  };

  loadView(viewId, catalogId, sceneId) {
    const { viewSettings } = this.props;

    if (viewId && catalogId) {
      viewActions.preGetView({ viewId, catalogId, sceneId });

      const defaultSettings = viewSettings && viewSettings.getIn([catalogId, 'views', '0']);
      const newViewSettings = viewSettings && viewSettings.getIn([catalogId, 'views', '$new']);

      // Скипаем повторную инициализацию, если $new уже есть в appState
      // (например, при открытии/закрытии модалки из этого же вида).
      if (viewId === '$new' && newViewSettings) {
        return;
      }

      if (viewId === '$new') {
        // Инициализируем $new из вида "Все записи" (0) только один раз,
        // когда $new ещё не создан в appState.
        if (defaultSettings) {
          viewSettingsActions.mergeViewSettingsToAppState({ catalogId, viewId: '$new', settings: defaultSettings });
          return;
        }

        // Если "0" еще не загружен в appState, берем его с API.
        viewSettingsActions.loadViewSettings({ catalogId, viewId: '0', targetViewId: '$new' });
      } else {
        viewSettingsActions.loadViewSettings({ catalogId, viewId });
      }
    }
  }

  getDefaultView() {
    const { defaultViewId } = this.props;

    if (defaultViewId) {
      return Immutable.Map({ id: defaultViewId });
    }

    return Immutable.Map({ id: '0' });
  }

  getFieldsToRender = () => {
    const { historyFieldsSettings, historyFieldsOrder, catalog } = this.props;
    let fields = catalog && catalog.get('fields');
    if (!fields) {
      fields = Immutable.List();
    }
    if (!historyFieldsSettings) {
      return fields;
    }
    let res = fields.filter((field) => {
      const visible = historyFieldsSettings && historyFieldsSettings.getIn([field.get('id'), 'visible']);
      return visible === undefined ? true : visible;
    });
    if (historyFieldsOrder && historyFieldsOrder.size > 0) {
      res = res.sortBy((field) => {
        const idx = historyFieldsOrder.indexOf(field.get('id'));
        return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
      });
    }
    return res;
  };

  componentDidMount() {
    const { viewId, catalogId, sceneId } = this.props;
    this.loadView(viewId, catalogId, sceneId);
  }

  componentDidUpdate(prevProps) {
    const { viewId, catalogId, sceneId, shouldClose, isActive } = this.props;

    if ((catalogId && catalogId !== prevProps.catalogId) || (viewId && viewId !== prevProps.viewId)) {
      if (prevProps.viewId && prevProps.viewId !== viewId && prevProps.viewId !== '$new') {
        viewSettingsActions.mergeViewSettingsToAppState({
          catalogId: prevProps.catalogId,
          viewId: prevProps.viewId,
          settings: { hasChanges: false },
        });
      }
      this.loadView(viewId, catalogId, sceneId);
    }

    if (shouldClose && isActive) {
      sceneActions.deleteScene(sceneId);
    }
  }

  render() {
    const {
      catalogId,
      sceneId,
      viewId,
      catalog,
      onClose,
      openRecordsInModal,
      isWebForm,
      leftPanel,
      viewMode,
      location,
    } = this.props;

    /* редирект на вид по умолчанию */
    if (!viewId) {
      return <DefaultRedirect route={routes.view} params="viewId" object={this.getDefaultView()} />;
    }

    /* редирект на вид по умолчанию, если вид из URL не существует или недоступен */
    const viewsLoaded = catalog && catalog.get('viewsLoaded');
    if (viewsLoaded && viewId && viewId !== '0' && viewId !== '$new' && catalog && !catalog.getIn(['views', viewId])) {
      // After renaming, route can already point to the new view id while store/caches are still updating.
      // localStorage is updated immediately in rename handler, so use it to avoid redirecting to another default view.
      const lsDefaultViewId = localStorage.getItem(`catalog.${catalogId}.defaultViewId`) || '0';
      if (String(lsDefaultViewId) === String(viewId)) {
        return null;
      }

      const defaultView = this.getDefaultView().get('id');
      const pathname = location.pathname.replace(/\/view\/[^/]+/, `/view/${defaultView}`);
      const to = pathname + (location.search || '');
      return <Redirect to={to} />;
    }

    return (
      <NavRoute
        route={routes.view}
        render={() => (
          <div className={cn(styles.container, { [styles.containerModal]: openRecordsInModal })}>
            <ViewHeader
              catalogId={catalogId}
              sceneId={sceneId}
              viewId={viewId}
              viewMode={viewMode}
              catalog={catalog}
              isWebForm={isWebForm}
              leftPanel={leftPanel}
              openRecordsInModal={openRecordsInModal}
              onClose={onClose}
              /* MOVE inside HEADER */
              getFieldsToRender={this.getFieldsToRender}
            />

            <ViewBody
              sceneId={sceneId}
              catalogId={catalogId}
              viewId={viewId}
              isWebForm={isWebForm}
              leftPanel={leftPanel}
              /* если нужно рендерить с крестиком, то это значит что нужно открывать все данные в модальном окне */
              openRecordsInModal={openRecordsInModal}
              getFieldsToRender={this.getFieldsToRender}
            />
          </div>
        )}
      />
    );
  }
}

export default withRouter(
  connect(
    View,
    {
      catalogs: ['catalogs'],
      viewSettings: ['viewSettings', 'catalogs'],
      scenes: ['scenes'],
    },
    (props, { scenes, catalogs, viewSettings }) => {
      const { sceneId, catalogId, viewId } = props;
      const catalog = catalogs.get(catalogId);
      const shouldClose = scenes.getIn([sceneId, 'shouldClose']);

      const catalogViewSettings = viewSettings && viewSettings.getIn([catalogId, 'views', viewId]);
      const historyFieldsSettings = catalogViewSettings && catalogViewSettings.getIn([VIEW_MODES.HISTORY, 'fields']);
      const historyFieldsOrder = catalogViewSettings && catalogViewSettings.getIn([VIEW_MODES.HISTORY, 'fieldsOrder']);

      const defaultViewId = localStorage.getItem(`catalog.${catalogId}.defaultViewId`) || '0';

      const defaultViewMode = catalogs.getIn([catalogId, 'views', viewId, 'viewMode']) || VIEW_MODES.TABLE;
      const viewModeMatch = matchPath(props.location.pathname, { path: routes.viewMode.path });
      const viewMode = viewModeMatch?.params.viewMode || defaultViewMode;

      return {
        ...props,
        catalogId,
        catalog,
        historyFieldsSettings,
        historyFieldsOrder,
        defaultViewId,
        viewMode,
        shouldClose,
        scenes,
        viewSettings,
      };
    },
  ),
);
